/**
 * Products Utility Functions
 * Pure functions for product data manipulation
 */

export interface ProductPrice {
    id: string;
    amount: number;
    currency: string;
    recurring: boolean;
    interval?: string;
    intervalCount?: number;
    default?: boolean;
    currencyOptions: Record<string, { amount: number; currency: string }>;
}

export interface ProductVariant {
    id: string;
    name: string;
    description: string;
    sku?: string;
    weight?: number;
    imageUrl?: string;
    default?: boolean;
    prices: ProductPrice[];
    creditPrice: number | null
}

export interface Product {
    id: string;
    name: string;
    description: string;
    imageUrl?: string;
    variants: ProductVariant[];
}

export class ProductsUtils {
    /**
     * Find a variant by ID within products
     */
    static findVariant(products: Product[], variantId: string): ProductVariant | null {
        for (const product of products) {
            const variant = product.variants.find(v => v.id === variantId);
            if (variant) {
                return variant;
            }
        }
        return null;
    }

    /**
     * Get price for a variant in a specific currency
     */
    static getVariantPrice(
        variant: ProductVariant,
        currency = 'USD'
    ): ProductPrice | null {
        const price = variant.prices.find(p => p.currency === currency);
        if (price) {
            return price;
        }

        // Fallback to default price
        const defaultPrice = variant.prices.find(p => p.default);
        if (defaultPrice) {
            return defaultPrice;
        }

        // Return first available price
        return variant.prices[0] || null;
    }

    /**
     * Get all variants from products
     */
    static getAllVariants(products: Product[]): { product: Product; variant: ProductVariant }[] {
        const allVariants: { product: Product; variant: ProductVariant }[] = [];
        for (const product of products) {
            if (product.variants) {
                for (const variant of product.variants) {
                    allVariants.push({ product, variant });
                }
            }
        }
        return allVariants;
    }

    /**
     * Filter variants by predicate
     */
    static filterVariants(
        products: Product[],
        predicate: (variant: ProductVariant, product: Product) => boolean
    ): { product: Product; variant: ProductVariant }[] {
        const filtered: { product: Product; variant: ProductVariant }[] = [];
        for (const product of products) {
            if (product.variants) {
                for (const variant of product.variants) {
                    if (predicate(variant, product)) {
                        filtered.push({ product, variant });
                    }
                }
            }
        }
        return filtered;
    }
}
